import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { Candidate } from "@/types/candidate";
import { CandidatesFilterProvider } from "@/contexts/CandidatesFilterContext";
import { CandidatesContent } from "@/components/candidates/CandidatesContent";
import { CandidatesActions } from "@/components/candidates/CandidatesActions";
import { CandidatesFilterWrapper } from "@/components/candidates/CandidatesFilterWrapper";
import { useSkillsFilter } from "@/hooks/candidates/useSkillsFilter";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { BulkImportModal } from "@/components/candidates/BulkImportModal";
import { CandidateFormData } from "@/utils/candidate";
import { usePersistedViewMode } from "@/utils/viewMode";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ToastAction } from "@/components/ui/toast";
import { LINKEDIN_NOT_CONNECTED, readLinkedInFetchError } from "@/utils/candidate/linkedinUtils";

const Candidates = () => {
	usePageTitle("Candidates");
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
	const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
	const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [prefillFormData, setPrefillFormData] = useState<CandidateFormData | null>(null);
	const [viewMode, setViewMode] = usePersistedViewMode("candidates");
	const [isProcessingLinkedIn, setIsProcessingLinkedIn] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	
	const { skills: allSkills, loadSkills } = useSkillsFilter();

	// For status, we use a fixed list
	const allStatuses = ["new", "looking", "open", "unavailable", "hired"];

	// Direct function to fetch LinkedIn data
	const fetchLinkedInData = async (linkedinUrl: string) => {
		//console.log("[CandidatesPage] Direct fetch of LinkedIn data for:", linkedinUrl);
		setIsProcessingLinkedIn(true);
		
		try {
			// Show processing toast
			toast({
				title: "Processing LinkedIn Profile",
				description: "Fetching data from LinkedIn API...",
			});
			
			// Call fetch-linkedin-profile edge function directly
			const { data: profileData, error } = await supabase.functions.invoke('fetch-linkedin-profile', {
				body: { profileUrl: linkedinUrl },
				headers: { 'Content-Type': 'application/json' }
			});
			
			//console.log("[CandidatesPage] Edge function response:", profileData);
			
			if (error) {
				console.error("[CandidatesPage] Edge function error:", error);
				const details = await readLinkedInFetchError(error);
				if (details.code === LINKEDIN_NOT_CONNECTED)
					toast({
						title: t("candidates.newCandidateModal.linkedinNotConnected"),
						description: t("candidates.newCandidateModal.linkedinNotConnectedDescription"),
						variant: "destructive",
						action: (
							<ToastAction altText={t("candidates.newCandidateModal.openIntegrations")} onClick={() => navigate("/settings?tab=integrations")}>
								{t("candidates.newCandidateModal.openIntegrations")}
							</ToastAction>
						)
					});
				else
					toast({
						title: "LinkedIn Import Failed",
						description: details.message || error.message || "Failed to fetch LinkedIn data",
						variant: "destructive"
					});
				return null;
			}
			
			if (profileData && profileData.success) {
				toast({
					title: "LinkedIn Profile Imported",
					description: profileData.import?.message || "LinkedIn data retrieved successfully",
				});
				
				// If data is available, prefill the form
				if (profileData.data) {
					const formData: CandidateFormData = {
						firstName: profileData.data.firstName || "",
						lastName: profileData.data.lastName || "",
						email: profileData.data.email || "",
						phone: profileData.data.phone || "",
						role: profileData.data.role || "",
						skills: profileData.data.skills || "",
						summary: profileData.data.summary || "",
						linkedin: profileData.data.linkedin || linkedinUrl,
						cvFile: null,
						linkedinJson: profileData.data.linkedinJson
					};
					
					setPrefillFormData(formData);
					setIsAddFormModalOpen(true);
					return formData;
				}
			} else {
				toast({
					title: "LinkedIn Import Failed",
					description: "Failed to process LinkedIn data",
					variant: "destructive"
				});
			}
		} catch (error) {
			console.error("[CandidatesPage] Error fetching LinkedIn data:", error);
			toast({
				title: "LinkedIn Import Failed",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive"
			});
		} finally {
			setIsProcessingLinkedIn(false);
		}
		
		return null;
	};

	// Check URL parameters on component mount for prefill data and modal trigger
	useEffect(() => {
		//console.log("[CandidatesPage] Checking URL parameters");
		const params = new URLSearchParams(location.search);
		const create = params.get('create');
		const linkedinUrl = params.get('linkedinUrl');

		if (create === 'true' && linkedinUrl) {
			//console.log('[CandidatesPage] Opening add form modal and fetching LinkedIn data due to URL parameter');

			// Clean up URL parameters after using them
			navigate(location.pathname, { replace: true });

			// Fetch LinkedIn data directly
			fetchLinkedInData(linkedinUrl);
		}
	}, [location.search, navigate, location.pathname]);

	const handleCandidateAdded = (newCandidate: Candidate) => {
		setRefreshTrigger(prev => prev + 1);
		setIsAddFormModalOpen(false);
		setPrefillFormData(null);
		
		// Navigate to the new candidate's detail page
		navigate(`/candidates/${newCandidate.id}`);
	};

	const handleManualCreate = () => {
		setIsSelectionModalOpen(false);
		setPrefillFormData(null);
		setIsAddFormModalOpen(true);
	};

	const handleCVUpload = (formData: CandidateFormData) => {
		setIsSelectionModalOpen(false);
		setPrefillFormData(formData);
		setIsAddFormModalOpen(true);
	};

	return (
		<CandidatesFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
			<div>
				<PageHeader 
					title={t("candidates.title")}
					actions={
						<CandidatesActions 
							isGenerating={isGenerating || isProcessingLinkedIn}
							setIsGenerating={setIsGenerating}
							fetchCandidates={async () => setRefreshTrigger(prev => prev + 1)}
							onAddCandidate={() => setIsSelectionModalOpen(true)}
							onBulkImport={() => setIsBulkImportModalOpen(true)}
						/>
					}
				/>
				
				<CandidatesFilterWrapper
					allSkills={allSkills}
					allStatuses={allStatuses}
					onSkillsFilterOpen={loadSkills}
				/>

				<CandidatesContent
					isLoading={isProcessingLinkedIn}
					onCandidatesChange={() => setRefreshTrigger(prev => prev + 1)}
				/>
				
				<AddCandidateSelectionModal
					isOpen={isSelectionModalOpen}
					onClose={() => setIsSelectionModalOpen(false)}
					onManualCreate={handleManualCreate}
					onCVUpload={handleCVUpload}
				/>
				
				<BulkImportModal
					isOpen={isBulkImportModalOpen}
					onClose={() => setIsBulkImportModalOpen(false)}
					onImportComplete={() => setRefreshTrigger(prev => prev + 1)}
				/>
				
				<AddCandidateForm 
					isOpen={isAddFormModalOpen}
					onClose={() => setIsAddFormModalOpen(false)}
					onCandidateAdded={handleCandidateAdded}
					initialFormData={prefillFormData}
				/>
			</div>
		</CandidatesFilterProvider>
	);
};

export default Candidates;
